import { useEffect } from "react";
import { useSession } from "@/hooks/auth-hooks";
import { authClient } from "@/lib/auth-client";

const hasGoogleClientId = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;
const oneTapMaskId = "agentreacher-google-one-tap-mask";
const lighthouseUserAgent = "Chrome-Lighthouse";

interface GoogleOneTapProps {
	callbackURL?: string;
}

export function GoogleOneTap({
	callbackURL = "/choose-plan",
}: GoogleOneTapProps) {
	const { user, isPending } = useSession();

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (window.navigator.userAgent.includes(lighthouseUserAgent)) return;

		const mask = document.createElement("div");
		mask.id = oneTapMaskId;
		mask.setAttribute("aria-hidden", "true");
		Object.assign(mask.style, {
			display: "none",
			position: "fixed",
			pointerEvents: "none",
			zIndex: "2147483647",
		});
		document.body.appendChild(mask);

		const updateMask = () => {
			const iframe = document.querySelector<HTMLIFrameElement>(
				'iframe#credential_picker_iframe, iframe[src*="accounts.google.com/gsi/iframe/select"]',
			);

			if (!iframe) {
				mask.style.display = "none";
				return;
			}

			const rect = iframe.getBoundingClientRect();
			const isBottomSheet =
				rect.width >= window.innerWidth * 0.8 &&
				window.innerHeight - rect.bottom < 4;

			if (!isBottomSheet) {
				mask.style.display = "none";
				return;
			}

			const background =
				getComputedStyle(document.documentElement)
					.getPropertyValue("--background")
					.trim() || "#0a0a0a";

			mask.style.display = "block";
			mask.style.left = `${rect.left}px`;
			mask.style.top = `${rect.top}px`;
			mask.style.width = `${rect.width}px`;
			mask.style.height = "18px";
			mask.style.backgroundColor = background;
		};

		const observer = new MutationObserver(updateMask);
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["style", "class"],
		});

		window.addEventListener("resize", updateMask);
		window.addEventListener("orientationchange", updateMask);
		updateMask();

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", updateMask);
			window.removeEventListener("orientationchange", updateMask);
			mask.remove();
		};
	}, []);

	useEffect(() => {
		if (!hasGoogleClientId) return;
		if (isPending) return;
		if (user) return;
		if (typeof window === "undefined") return;
		if (window.navigator.userAgent.includes(lighthouseUserAgent)) return;

		authClient
			.oneTap({
				callbackURL,
				onPromptNotification: (notification) => {
					if (!import.meta.env.DEV) return;
					const n = notification as
						| {
								getMomentType?: () => string;
								getNotDisplayedReason?: () => string;
								getSkippedReason?: () => string;
								getDismissedReason?: () => string;
						  }
						| undefined;
					console.debug("[GoogleOneTap] prompt not shown", {
						moment: n?.getMomentType?.(),
						notDisplayed: n?.getNotDisplayedReason?.(),
						skipped: n?.getSkippedReason?.(),
						dismissed: n?.getDismissedReason?.(),
					});
				},
			})
			.catch((error) => {
				if (import.meta.env.DEV) {
					console.debug("[GoogleOneTap] error", error);
				}
			});
	}, [callbackURL, isPending, user]);

	return null;
}
